import React from 'react';
import { University } from '../../../types';

interface PostCollegeTabProps {
  university: University;
}

const PostCollegeTab: React.FC<PostCollegeTabProps> = ({ university }) => {
  // Format currency
  const formatCurrency = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `$${num.toLocaleString()}`;
  };

  // Format percentage (for values that are already percentages like graduation_rate)
  const formatPercentage = (num: number | null | undefined) => {
    if (num === null || num === undefined) return 'N/A';
    return `${Math.round(num)}%`;
  };

  // Format decimal to percentage (for values like employed_2_years_after_graduation)
  const formatDecimalToPercentage = (decimal: number | null | undefined) => {
    if (decimal === null || decimal === undefined) return 'N/A';
    return `${Math.round(decimal * 100)}%`;
  };

  return (
    <div className="tab-content" style={{ width: '100%', maxWidth: 'none', margin: 0, padding: 0 }}>
      <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">Post-College Life</h3>
      
      {/* Career Outcomes Overview */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        {/* Graduation Rate Card */}
        <div className="bg-green-50/50 dark:bg-green-900/20 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-green-500 dark:text-green-400 text-2xl mr-3">
              <i className="fas fa-graduation-cap"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Graduation Rate</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">{formatPercentage(university.graduation_rate)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">4-year completion</p>
          </div>
        </div>

        {/* Employment Rate Card */}
        <div className="bg-blue-50/50 dark:bg-blue-900/20 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-blue-500 dark:text-blue-400 text-2xl mr-3">
              <i className="fas fa-briefcase"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Employment Rate</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{formatDecimalToPercentage(university.employed_2_years_after_graduation)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Within 6 months</p>
          </div>
        </div>

        {/* Average Income Card */}
        <div className="bg-purple-50/50 dark:bg-purple-900/20 rounded-xl p-6">
          <div className="flex items-center mb-4">
            <div className="text-purple-500 dark:text-purple-400 text-2xl mr-3">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Average Income</h4>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(university.median_earnings_6_years_after_graduation)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Starting salary</p>
          </div>
        </div>
      </div>

      {/* Career Success Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8">
        <div className="flex items-center mb-6">
          <div className="text-orange-500 dark:text-orange-400 text-2xl mr-3">
            <i className="fas fa-chart-line"></i>
          </div>
          <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">Career Success Metrics</h4>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="text-center p-4 bg-orange-50/50 dark:bg-orange-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Employers</h5>
            <p className="text-lg font-bold text-orange-600 dark:text-orange-400">Google, Apple, Microsoft</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Leading tech companies</p>
          </div>
          <div className="text-center p-4 bg-orange-50/50 dark:bg-orange-900/20 rounded-lg">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Graduate School</h5>
            <p className="text-lg font-bold text-orange-600 dark:text-orange-400">35%</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Pursue advanced degrees</p>
          </div>
        </div>
      </div>

      {/* Alumni Success Stories */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <div className="flex items-start">
          <div className="text-blue-500 dark:text-blue-400 text-xl mr-3 mt-1">
            <i className="fas fa-trophy"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 dark:text-blue-300 mb-3">Alumni Success</h4>
            <div className="grid grid-cols-2 gap-4 text-blue-800 dark:text-blue-300">
              <div>
                <p className="font-semibold mb-2">Notable Alumni:</p>
                <ul className="space-y-1 text-sm">
                  <li>• 30+ Nobel Prize winners</li>
                  <li>• 17 astronauts</li>
                  <li>• 4 U.S. Presidents</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Career Impact:</p>
                <ul className="space-y-1 text-sm">
                  <li>• {formatDecimalToPercentage(university.employed_2_years_after_graduation)} employed within 6 months</li>
                  <li>• {formatCurrency(university.median_earnings_6_years_after_graduation)} average starting salary</li>
                  <li>• {formatPercentage(university.graduation_rate)} graduation rate</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCollegeTab;
